/**
 * Biểu đồ của Dashboard RPA, dựng trên bộ SVG thuần ở `nhat-ky-truy-vet/chung/components/BieuDo.tsx` như màn Sự cố
 * (kho không có thư viện biểu đồ). Loạt và màu cố định ở `LOAT_LUOT` (`constants.ts`).
 */
import { RobotOutlined } from '@ant-design/icons';

import { Badge, Empty } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { ngayVN, soVN } from '../../../../lib/dinh-dang';
import { BieuDoVanh, BieuDoXepChong, ChuGiai } from '../../../nhat-ky-truy-vet/chung/components/BieuDo';
import { LOAT_LUOT, TRANG_THAI_ROBOT } from '../../constants';
import { latLuot, phanTram, SO_ROBOT_HIEN } from '../../nghiep-vu/tong-quan';
import type { DemLuot, RobotTongQuan } from '../../types';

function useLoat() {
  const { t } = useT();
  return LOAT_LUOT.map((l) => ({ nhan: t(l.khoa), mau: l.mau }));
}

/** Cột xếp chồng theo ngày (cũ trước). */
export function CotTheoNgay({ ngay, rong }: { ngay: (DemLuot & { date: string })[]; rong: string }) {
  const { t, locale } = useT();
  const loat = useLoat();
  if (ngay.every((d) => d.total === 0)) return <Empty title={rong} className="py-6" />;
  return (
    <>
      <BieuDoXepChong
        nhan={t('rpaTongQuan.theoNgay')}
        nhanTong={t('rpaTongQuan.tong')}
        loat={loat}
        dinhDang={(gt) => t('rpaTongQuan.soLuotThe', { so: soVN(gt, locale) })}
        cot={ngay.map((d) => ({
          nhan: ngayVN(d.date).slice(0, 5),
          tieuDe: ngayVN(d.date),
          lat: latLuot(d),
          tong: d.total,
          moTa: t('rpaTongQuan.moTaCot', { ngay: ngayVN(d.date), so: soVN(d.total, locale) }),
        }))}
      />
      <ChuGiai muc={loat} />
    </>
  );
}

/** Vành thành công / lỗi trên số lượt đã kết thúc (không tính lượt bị dừng tay). */
export function VanhTyLe({ thanhCong, loi, tyLe }: { thanhCong: number; loi: number; tyLe: number }) {
  const { t, locale } = useT();
  const xong = thanhCong + loi;
  if (xong === 0) return <Empty title={t('rpaTongQuan.chuaCoLuotXong')} className="py-6" />;
  const tc = phanTram(tyLe);
  return (
    <div className="flex flex-col items-center gap-3">
      <BieuDoVanh
        nhan={t('rpaTongQuan.tyLeTieuDe')}
        giua={`${soVN(tc, locale)}%`}
        phu={t('rpaTongQuan.thanhCongNgan')}
        dinhDang={(gt) => t('rpaTongQuan.soLuotThe', { so: soVN(gt, locale) })}
        lat={[
          { nhan: t(LOAT_LUOT[0]!.khoa), giaTri: thanhCong, mau: LOAT_LUOT[0]!.mau },
          { nhan: t(LOAT_LUOT[1]!.khoa), giaTri: loi, mau: LOAT_LUOT[1]!.mau },
        ]}
      />
      <div className="grid w-full grid-cols-2 gap-2 text-center">
        <div className="rounded-lg bg-success-50 px-3 py-2">
          <p className="text-lg font-semibold tabular-nums text-success-700">{soVN(tc, locale)}%</p>
          <p className="text-xs text-content-secondary">{t('rpaTongQuan.tyLeThanhCong')}</p>
        </div>
        <div className="rounded-lg bg-danger-50 px-3 py-2">
          <p className="text-lg font-semibold tabular-nums text-danger-700">{soVN(phanTram(100 - tyLe), locale)}%</p>
          <p className="text-xs text-content-secondary">{t('rpaTongQuan.tyLeLoi')}</p>
        </div>
      </div>
      <p className="text-center text-xs text-content-muted">{t('rpaTongQuan.tongPhien', { so: soVN(xong, locale) })}</p>
    </div>
  );
}

/** Thanh ngang xếp chồng cho từng Project; dài theo Project nhiều lượt nhất. */
export function ThanhTheoProject({ dong, rong }: { dong: (DemLuot & { projectId: string; projectName: string })[]; rong: string }) {
  const { t, locale } = useT();
  const loat = useLoat();
  if (!dong.length) return <Empty title={rong} className="py-6" />;
  const lonNhat = Math.max(1, ...dong.map((d) => d.total));
  const ten = (d: { projectId: string; projectName: string }) => (d.projectId === '—' ? t('rpaTongQuan.chuaGanProject') : d.projectName || d.projectId);
  return (
    <>
      <ul aria-label={t('rpaTongQuan.theoProject')} className="max-h-72 space-y-3 overflow-y-auto overscroll-contain pr-2">
        {dong.map((d) => {
          const lat = latLuot(d);
          return (
            <li key={d.projectId} className="text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-content-secondary" title={ten(d)}>
                  {ten(d)}
                </span>
                <b className="shrink-0 tabular-nums text-content-primary">
                  {t('rpaTongQuan.okLoi', { ok: soVN(d.success, locale), loi: soVN(d.failed + d.timeout, locale) })}
                </b>
              </div>
              <span
                role="img"
                aria-label={t('rpaTongQuan.moTaProject', {
                  project: ten(d),
                  chiTiet: lat.map((v, i) => `${loat[i]!.nhan} ${soVN(v, locale)}`).join(', '),
                })}
                className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-subtle"
                style={{ width: `${Math.max((d.total / lonNhat) * 100, 2)}%` }}
              >
                {lat.map((v, i) =>
                  v > 0 ? <span key={i} className="block h-full" style={{ width: `${(v / d.total) * 100}%`, backgroundColor: loat[i]!.mau }} /> : null,
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <ChuGiai muc={loat} />
    </>
  );
}

/** Mỗi Robot một dòng: Robot đang chạy trước, còn lại theo lượt gần nhất (máy chủ đã sắp). */
export function DanhSachRobot({ robots }: { robots: RobotTongQuan[] }) {
  const { t } = useT();
  if (!robots.length) return <Empty icon={RobotOutlined} title={t('rpaTongQuan.chuaCoRobot')} className="py-6" />;
  return (
    <ul className="divide-y divide-default">
      {robots.slice(0, SO_ROBOT_HIEN).map((r) => {
        const v = TRANG_THAI_ROBOT[r.status] ?? TRANG_THAI_ROBOT.unknown;
        return (
          <li key={r.robotId} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm text-content-primary" title={r.robotName || r.robotId}>
                {r.robotName || r.robotId}
              </p>
              <p className="truncate text-xs text-content-muted" title={r.lastError ?? undefined}>
                {[r.machineName, r.workflowName].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="flex min-w-0 shrink-0 items-center gap-2">
              <span className="hidden max-w-[8rem] truncate text-xs text-content-muted sm:inline">{r.projectName || r.projectId}</span>
              <Badge variant={v.variant} className="whitespace-nowrap">
                {t(v.khoa)}
              </Badge>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
